<template>
  <section class="filter-panel mb-1">
    <b-button
      variant="link"
      block
      class="filter-panel__toggle"
      :aria-expanded="showFilters ? 'true' : 'false'"
      @click="showFilters = !showFilters"
    >
      <span class="filter-panel__heading">
        <span class="filter-panel__icon">
          <feather-icon size="19" icon="FilterIcon" />
        </span>
        <span class="filter-panel__heading-copy">
          <span class="filter-panel__title">
            {{ $t("filterPanel.title") }}
          </span>
          <small class="filter-panel__description">
            {{ $t("filterPanel.description") }}
          </small>
        </span>
      </span>

      <span class="filter-panel__toggle-meta">
        <span v-if="activeFilterCount" class="filter-panel__active-count">
          {{
            $tc(
              "filterPanel.activeCount",
              activeFilterCount,
              { count: activeFilterCount },
            )
          }}
        </span>
        <span class="filter-panel__chevron">
          <feather-icon
            size="18"
            :icon="showFilters ? 'ChevronUpIcon' : 'ChevronDownIcon'"
          />
        </span>
      </span>
    </b-button>

    <b-collapse v-model="showFilters">
      <b-card class="filter-panel__body rounded-0 border-0 mb-0">
        <b-form @submit.prevent="handleForm" @reset="handleReset">
          <b-row class="filter-panel__fields">
            <b-col
              v-for="(field, key) in finalFields"
              :key="key"
              cols="12"
              md="6"
              lg="4"
              xl="3"
              class="filter-panel__field"
            >
              <label
                class="filter-panel__label"
                :for="
                  field.type === 'select'
                    ? `select-${field.name}`
                    : field.name
                "
              >
                {{ getPlaceholder(field) }}
              </label>

              <component
                v-if="field.type !== 'select'"
                :is="getFieldComponent(field)"
                :id="field.name"
                :name="field.name"
                :label="field.title"
                class="filter-panel__control form-control"
                :placeholder="getPlaceholder(field)"
                v-model="form[field.name]"
                :config="field.config"
                v-bind="getAdditionalProps(field)"
              />

              <v-select
                v-else
                :id="`select-${field.name}`"
                v-model="form[field.name]"
                :options="field.options"
                label="label"
                :reduce="(option) => option.value"
                :placeholder="getPlaceholder(field)"
                :taggable="false"
                :filterable="!hasRemotePagination(field.name)"
                :multiple="field.multiple"
                :close-on-select="field.close !== false"
                :open-on-focus="false"
                :disable-on-close="true"
                class="filter-panel__select"
                @open="handleSelectOpen(field.name)"
                @close="handleSelectClose(field.name)"
                @search="handleSelectSearch($event, field.name)"
              >
                <template #option="{ label, index }">
                  <div
                    :class="{
                      'last-option': index === field.options.length - 1,
                    }"
                    class="filter-panel__option"
                  >
                    {{ label }}
                  </div>
                </template>
                <template
                  v-if="hasRemotePagination(field.name)"
                  #list-footer
                >
                  <li
                    v-show="
                      hasSelectNextPage(field.name) ||
                      isSelectLoading(field.name)
                    "
                    :ref="`load-${field.name}`"
                    class="select-loader"
                  >
                    {{ $t("loadingOption") }}
                  </li>
                </template>
              </v-select>
            </b-col>
          </b-row>
          <div class="filter-panel__footer">
            <small class="filter-panel__hint">
              <feather-icon icon="InfoIcon" size="14" />
              {{ $t("filterPanel.hint") }}
            </small>

            <div class="filter-panel__actions">
              <b-button
                type="reset"
                variant="outline-secondary"
              >
                <feather-icon icon="RotateCcwIcon" size="16" class="mr-50" />
                {{ $t("filterPanel.clear") }}
              </b-button>

              <b-button
                type="submit"
                variant="primary"
                :disabled="actionsDisabled"
              >
                <feather-icon icon="SearchIcon" size="16" class="mr-50" />
                {{ $t("filterPanel.apply") }}
              </b-button>
            </div>
          </div>
        </b-form>
      </b-card>
    </b-collapse>
  </section>
</template>

<script>
import {
  BButton,
  BCollapse,
  BCard,
  BForm,
  BFormInput,
  BFormSelect,
  BFormDatepicker,
  BRow,
  BCol,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";

export default {
  components: {
    BButton,
    BCollapse,
    BCard,
    BForm,
    BFormInput,
    BFormSelect,
    BFormDatepicker,
    BRow,
    BCol,
    vSelect,
    flatPickr,
  },
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    model: {
      type: String,
      default: "",
    },
    selectPagination: {
      type: Object,
      default: () => ({}),
    },
    actionsDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showFilters: true,
      form: {},
      selectObservers: {},
      selectSearchTimers: {},
    };
  },
  computed: {
    finalFields() {
      return this.fields;
    },
    activeFilterCount() {
      return Object.values(this.form).filter((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return (
          value !== null &&
          value !== undefined &&
          String(value).trim() !== ""
        );
      }).length;
    },
  },
  methods: {
    getFieldComponent(field) {
      const componentMap = {
        text: "b-form-input",
        search: "b-form-input",
        select: "b-form-select",
        date: "b-form-datepicker",
        number: "b-form-input",
        range: "flat-pickr",
      };
      return componentMap[field.type] || "b-form-input";
    },

    getPlaceholder(field) {
      let placehold = this.$t(
        `pages.${this.model}.table.tableFilter.${field.placeholder}`,
      );
      return placehold;
    },

    getAdditionalProps(field) {
      const props = {
        placeholder: this.$t(
          `pages.${this.model}.table.tableFilter.${field.placeholder}`,
        ),
      };

      if (field.type === "number") {
        props.type = "number";
        props.min = field.min;
      } else if (field.type === "select") {
        props.options = field.options;
      } else if (field.type === "date") {
        props.type = "date";
        props.format = "YYYY-MM-DD";
      } else if (field.type === "search") {
        props.type = "search";
      } else {
        props.type = "text";
      }
      return props;
    },

    handleForm() {
      if (this.actionsDisabled) {
        return;
      }
      this.$emit("filter-applied", this.form);
    },

    handleSelectInput(label, field, e) {
      // if (field.multiple) {
      //   const selectedLabels = field.options.filter(option => value.includes(option.value))
      //   .map(option => option.label);
      //   this.form[label] = selectedLabels.join(", ")
      // }
      // this.$emit("select-changed", { label, value });
    },

    handleSelectSearch(value, label) {
      if (!this.hasRemotePagination(label)) {
        return;
      }

      clearTimeout(this.selectSearchTimers[label]);
      this.$set(
        this.selectSearchTimers,
        label,
        setTimeout(() => {
          this.$emit("select-search", {
            fieldName: label,
            value,
          });
        }, 300),
      );
    },

    async handleSelectOpen(fieldName) {
      if (
        !this.hasRemotePagination(fieldName) ||
        !this.hasSelectNextPage(fieldName) ||
        typeof IntersectionObserver === "undefined"
      ) {
        return;
      }

      await this.$nextTick();
      const target = this.getSelectLoader(fieldName);
      if (!target) {
        return;
      }

      if (!this.selectObservers[fieldName]) {
        this.$set(
          this.selectObservers,
          fieldName,
          new IntersectionObserver((entries) =>
            this.infiniteScrollSelect(fieldName, entries),
          ),
        );
      }

      this.selectObservers[fieldName].observe(target);
    },

    handleSelectClose(fieldName) {
      clearTimeout(this.selectSearchTimers[fieldName]);
      if (this.selectObservers[fieldName]) {
        this.selectObservers[fieldName].disconnect();
      }
    },

    infiniteScrollSelect(fieldName, [{ isIntersecting, target }]) {
      if (
        !isIntersecting ||
        this.isSelectLoading(fieldName) ||
        !this.hasSelectNextPage(fieldName)
      ) {
        return;
      }

      const list = target.offsetParent;
      const scrollTop = list ? list.scrollTop : 0;

      this.$emit("select-load-more", {
        fieldName,
        done: async () => {
          await this.$nextTick();
          if (list) {
            list.scrollTop = scrollTop;
          }

          const observer = this.selectObservers[fieldName];
          const currentTarget = this.getSelectLoader(fieldName);

          if (observer) {
            observer.disconnect();
            if (
              currentTarget &&
              this.hasSelectNextPage(fieldName)
            ) {
              observer.observe(currentTarget);
            }
          }
        },
      });
    },

    getSelectLoader(fieldName) {
      const loader = this.$refs[`load-${fieldName}`];
      return Array.isArray(loader) ? loader[0] : loader;
    },

    hasRemotePagination(fieldName) {
      return Boolean(this.selectPagination[fieldName]);
    },

    hasSelectNextPage(fieldName) {
      return Boolean(this.selectPagination[fieldName]?.hasNextPage);
    },

    isSelectLoading(fieldName) {
      return Boolean(this.selectPagination[fieldName]?.loading);
    },

    handleReset() {
      this.form = {};
      this.$emit("filter-reset");
    },
  },
  beforeDestroy() {
    Object.values(this.selectObservers).forEach((observer) => {
      observer.disconnect();
    });
    Object.values(this.selectSearchTimers).forEach((timer) => {
      clearTimeout(timer);
    });
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

@import "@core/scss/vue/libs/vue-flatpicker.scss";

.filter-panel {
  overflow: visible;
  border: 1px solid #ebe9f1;
  border-radius: 0.75rem;
  background: #fff;
  box-shadow: 0 3px 12px rgba(34, 41, 47, 0.05);
}

.filter-panel__toggle.btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 68px;
  padding: 0.85rem 1rem;
  border: 0;
  border-radius: 0.75rem;
  color: #5e5873;
  text-align: left;
  text-decoration: none;
  box-shadow: none;
}

.filter-panel__toggle.btn:hover,
.filter-panel__toggle.btn:focus {
  color: #5e5873;
  text-decoration: none;
  background: rgba(115, 103, 240, 0.035);
  box-shadow: none;
}

.filter-panel__heading,
.filter-panel__toggle-meta {
  display: flex;
  align-items: center;
}

.filter-panel__heading {
  min-width: 0;
  gap: 0.75rem;
}

.filter-panel__icon,
.filter-panel__chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.filter-panel__icon {
  width: 40px;
  height: 40px;
  border-radius: 0.6rem;
  background: rgba(115, 103, 240, 0.1);
  color: #7367f0;
}

.filter-panel__heading-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.filter-panel__title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
}

.filter-panel__description {
  margin-top: 0.15rem;
  overflow: hidden;
  color: #b9b9c3;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-panel__toggle-meta {
  gap: 0.65rem;
  margin-left: 1rem;
}

.filter-panel__active-count {
  padding: 0.3rem 0.65rem;
  border-radius: 2rem;
  background: rgba(40, 199, 111, 0.12);
  color: #1f9d59;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.filter-panel__chevron {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f8f8f8;
  color: #6e6b7b;
}

.filter-panel__body .card-body {
  padding: 1rem;
  border-top: 1px solid #ebe9f1;
}

.filter-panel__fields {
  margin-right: -0.5rem;
  margin-left: -0.5rem;
}

.filter-panel__field {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
  margin-bottom: 1rem;
}

.filter-panel__label {
  display: block;
  margin-bottom: 0.4rem;
  color: #5e5873;
  font-size: 0.82rem;
  font-weight: 600;
}

.filter-panel__control.form-control,
.filter-panel__select .vs__dropdown-toggle {
  min-height: 42px;
  border-color: #d8d6de;
  border-radius: 0.45rem;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.filter-panel__control.form-control:focus,
.filter-panel__select.vs--open .vs__dropdown-toggle {
  border-color: #7367f0;
  box-shadow: 0 0 0 0.15rem rgba(115, 103, 240, 0.12);
}

.filter-panel__select .vs__dropdown-toggle {
  padding: 0 0 3px;
}

.filter-panel__select .vs__selected-options {
  align-items: center;
  min-height: 38px;
  padding-left: 0.45rem;
}

.filter-panel__select .vs__search,
.filter-panel__select .vs__search:focus {
  margin-top: 0;
}

.filter-panel__select .vs__selected {
  margin-top: 3px;
}

.filter-panel__select .vs__dropdown-menu {
  border-color: #ebe9f1;
  border-radius: 0 0 0.45rem 0.45rem;
  box-shadow: 0 8px 24px rgba(34, 41, 47, 0.12);
}

.filter-panel__option {
  display: flex;
  align-items: center;
  min-height: 34px;
}

.filter-panel__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid #ebe9f1;
}

.filter-panel__hint {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #b9b9c3;
}

.filter-panel__actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.filter-panel__actions .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 118px;
}

.select-loader {
  padding: 0.75rem;
  text-align: center;
  color: #b8b8b8;
}

@media (max-width: 767.98px) {
  .filter-panel__toggle.btn {
    min-height: 62px;
    padding: 0.75rem;
  }

  .filter-panel__description {
    max-width: 55vw;
  }

  .filter-panel__active-count {
    display: none;
  }

  .filter-panel__body .card-body {
    padding: 0.85rem;
  }

  .filter-panel__footer {
    align-items: stretch;
    flex-direction: column;
  }

  .filter-panel__hint {
    align-self: flex-start;
  }

  .filter-panel__actions {
    width: 100%;
  }

  .filter-panel__actions .btn {
    flex: 1 1 50%;
    min-width: 0;
  }
}

@media (max-width: 420px) {
  .filter-panel__description {
    display: none;
  }

  .filter-panel__actions {
    flex-direction: column-reverse;
  }

  .filter-panel__actions .btn {
    width: 100%;
  }
}
</style>
